// 🔧 vendor_view.js
// ✅ Purpose: Public page to show full details for a single vendor
// ✅ Features:
// - Uses standard public layout styling
// - Displays logo, name, category, booth number, description, and external URL
// - Loads vendor from /events/{eventId}/vendors/{vendorSlug}
// - Supports vendorSlug or document ID fallback
// - Adds event-specific SEO metadata
// - Fully block-commented for clarity and future devs

import { isEventPublic } from "../../utils/event_visibility";
import React, { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { db } from "../../firebase";
import { useEventContext } from "../../hooks/use_event_context";
import FullPageSpinner from "../../components/FullPageSpinner";
import { logger } from "../../utils/logger";
import SeoHead from "../../components/seo_head";
import { trackEvent } from "../../utils/analytics";
import { GA_EVENTS } from "../../services/analytics/ga_events";
import { sanitizeHtml } from "../../utils/sanitize_html";
import { getPowerupPath, resolvePowerupSnapshot } from "../../utils/powerup_urls";

const VendorView = () => {

  const {
    eventslug,
    eventId,
    eventLoading,
    ready,
    eventDoc,
  } = useEventContext({
    redirectIfNotFound: true,
  });

  const { vendorSlug } = useParams();
  const [searchParams] = useSearchParams();
  const vendorPaymentStatus = searchParams.get("vendor_payment");
  const [vendor, setVendor] = useState(null);
  const [loadingVendor, setLoadingVendor] = useState(true);

  useEffect(() => {
    if (!eventId || !vendorSlug) return;

    const fetchVendor = async () => {
      try {
        const vendorDoc = await resolvePowerupSnapshot(db, eventId, "vendor", vendorSlug);

        if (vendorDoc) {
          const vendorData = {
            id: vendorDoc.id,
            ...vendorDoc.data(),
          };

          const isLegacyVendor =
            vendorData.paymentStatus === undefined &&
            vendorData.approvalStatus === undefined &&
            vendorData.isApproved === undefined;

          const isApprovedVendor =
            vendorData.isApproved === true &&
            vendorData.approvalStatus === "approved" &&
            ["paid", "waived", "not_required"].includes(
              vendorData.paymentStatus
            );

          if (!isLegacyVendor && !isApprovedVendor) {
            setVendor(null);

            return;
          }

          setVendor(vendorData);

          window.scrollTo({
            top: 0,
            behavior: "smooth",
          });
        }
      } catch (err) {
        logger.error("❌ Error loading vendor:", {
          value: err,
        });
      } finally {
        setLoadingVendor(false);
      }
    };

    fetchVendor();
  }, [eventId, vendorSlug]);

    useEffect(() => {
    if (!ready || !eventId || !vendor?.id) return;

    trackEvent({
      action: GA_EVENTS.VENDOR_VIEWED,
      parameters: {
        event_slug: eventslug,
        event_id: eventId,
        vendor_id: vendor.id,
        vendor_name: vendor.name || "",
        vendor_category: vendor.category || "",
        source_page: window.location.pathname,
      },
    });
  }, [ready, eventId, eventslug, vendor?.id]);

  const breadcrumbs = [
    { name: "Home", path: "/" },
    {
      name: eventDoc?.name || "Event",
      path: `/${eventslug}/event`,
    },
    {
      name: "Vendors",
      path: `/${eventslug}/vendors`,
    },
    {
      name: vendor?.name || "Vendor",
      path: getPowerupPath(eventslug, "vendor", vendor),
    },
  ];

  const vendorSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: vendor?.name,
    url: vendor?.url || undefined,
    description: vendor?.description || undefined,
    logo: vendor?.logoUrl || undefined,
    sameAs: vendor?.url
      ? [vendor.url]
      : undefined,    
  };

  const vendorSeo = {
    title:
      `${vendor?.name || "Vendor"} | ${
        eventDoc?.name || "Let's Go Vote"
      }`,

    description:
      vendor?.description ||
      `${vendor?.name || "Vendor"} is participating in ${
        eventDoc?.name || "this event"
      }.`,

    path:
      getPowerupPath(eventslug, "vendor", vendor),

    image:
      vendor?.logoUrl ||
      eventDoc?.eventLogo ||
      "https://letsgovote.app/images/social-share-default.png",

    noindex: !isEventPublic(eventDoc),
    breadcrumbs,
    structuredData: vendorSchema,
  };

  if (!ready || eventLoading || loadingVendor) {
    return <FullPageSpinner message="Loading Vendor..." />;
  }

  if (!vendor) {
    return (
      <section id="top-content" className="container-fluid py-5">
        <div className="container">
          <div className="alert alert-danger text-center">
            ❌ Vendor not found.
          </div>

          <div className="text-center">
            <Link
              to={`/${eventslug}/vendors`}
              className="btn btn-outline-primary"
            >
              ← Back to Vendors
            </Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <SeoHead {...vendorSeo} />

      <section id="top-content" className="container-fluid py-5">
        <div className="container" style={{ maxWidth: "1100px" }}>
          <div className="row mb-4">
            <div className="col text-start">
              <h1 className="display-5">🛍️ Vendor Spotlight</h1>

              <p className="lead text-muted">
                Learn more about this event vendor.
              </p>
            </div>
          </div>

          {vendorPaymentStatus === "success" && (
            <div className="alert alert-success">
              ✅ Vendor payment received. This vendor listing is now approved.
            </div>
          )}

          {vendorPaymentStatus === "cancelled" && (
            <div className="alert alert-warning">
              Vendor payment was cancelled. You can use the payment link again when ready.
            </div>
          )}
          
          <div className="white-section p-4 rounded shadow-sm bg-white">
            <div className="row align-items-center g-4">
              <div className="col-12 col-md-4 text-center">
                {vendor.logoUrl ? (
                  <img
                    src={vendor.logoUrl}
                    alt={vendor.name || "Vendor logo"}
                    className="img-fluid"
                    style={{
                      maxHeight: "220px",
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  <div className="border rounded bg-light p-5 text-muted">
                    No logo available
                  </div>
                )}
              </div>

              <div className="col-12 col-md-8">
                <h2 className="fw-bold mb-3">
                  {vendor.name || "Unnamed Vendor"}
                </h2>

                <div className="mb-3">
                  {vendor.category && (
                    <span className="badge bg-secondary fs-6 me-2">
                      {vendor.category}
                    </span>
                  )}

                  {vendor.boothNumber && (
                    <span className="badge bg-primary fs-6 me-2">
                      📍 {vendor.boothNumber}
                    </span>
                  )}

                  {vendor.featuredVendor && (
                    <span className="badge bg-success fs-6">
                      Featured Vendor
                    </span>
                  )}
                </div>

                {vendor.description && (
                  <div
                    className="mb-3 ql-editor p-0"
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(vendor.description),
                    }}
                  />
                )}

                <div className="d-flex flex-wrap gap-2">
                  {vendor.url && (
                    <a
                      href={vendor.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary"
                      onClick={() => {
                        trackEvent({
                          action: GA_EVENTS.VENDOR_WEBSITE_CLICKED,
                          parameters: {
                            event_slug: eventslug,
                            event_id: eventId,
                            vendor_id: vendor.id,
                            vendor_name: vendor.name || "",
                            vendor_category: vendor.category || "",
                            source_page: window.location.pathname,
                          },
                        });
                      }}
                    >
                      🌐 Visit Website
                    </a>
                  )}

                  <Link
                    to={`/${eventslug}/event`}
                    className="btn btn-outline-secondary"
                  >
                    🗺 View Event Map
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {(vendor.mapMarkerTitle || vendor.mapMarkerNote) && (
            <div className="card shadow-sm border-0 mt-4">
              <div className="card-header bg-white">
                <strong>🗺 Vendor Map Details</strong>
              </div>

              <div className="card-body">
                {vendor.mapMarkerTitle && (
                  <div className="fw-bold mb-2">
                    {vendor.mapMarkerTitle}
                  </div>
                )}

                {vendor.mapMarkerNote && (
                  <div className="text-muted">
                    {vendor.mapMarkerNote}
                  </div>
                )}

                {vendor.mapMarkerLabel && (
                  <div className="mt-3">
                    <span className="badge bg-primary">
                      Marker: {vendor.mapMarkerLabel}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="text-center mt-4">
            <Link
              to={`/${eventslug}/vendors`}
              className="btn btn-outline-primary"
            >
              ← Back to All Vendors
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default VendorView;
